import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth, hasMinRole } from "@/context/AuthContext";
import { PageHeader, Card, Loading, EmptyState } from "@/components/common";
import { WorkflowBadge, VisibilityBadge, StatusDomainStrip } from "@/components/insights/InsightStatusBadges";
import { WORKFLOW, EVENT_LABELS, fmtDateTime, isEditableWorkflow, isFinalized, isTerminal } from "@/lib/insightsApi";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, Paperclip, GitBranch, History, Clock, CheckCircle2, RotateCcw,
  XCircle, Upload, Download, ShieldAlert, Lock, Send, Pencil, Save, CalendarClock,
} from "lucide-react";
import { OUTCOME_LABELS } from "@/lib/meetingsApi";
import AdvancedWorkflowPanel from "@/components/insights/AdvancedWorkflowPanel";

const STAGE_ORDER = ["draft", "submitted", "in_review", "approved"];

export default function InsightDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [insight, setInsight] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [events, setEvents] = useState([]);
  const [agendaInfo, setAgendaInfo] = useState([]);
  const [considerations, setConsiderations] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // {action}
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({ title: "", summary: "", content: {} });
  const fileRef = useRef(null);

  const isApprover = hasMinRole(user?.role, "manager");
  const isAdmin = user?.role === "administrator";

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get(`/insights/${id}`);
      setInsight(data);
      setEdit({ title: data.title, summary: data.summary || "", content: { ...(data.content || {}) } });
      const [a, r, e] = await Promise.all([
        api.get(`/insights/${id}/attachments`).then((x) => x.data.attachments).catch(() => []),
        api.get(`/insights/${id}/revisions`).then((x) => x.data.revisions).catch(() => []),
        api.get(`/insights/${id}/events`).then((x) => x.data.events).catch(() => []),
      ]);
      setAttachments(a); setRevisions(r); setEvents(e);
      api.get(`/insights/${id}/agenda-info`).then((x) => setAgendaInfo(x.data.placements)).catch(() => setAgendaInfo([]));
      api.get(`/insights/${id}/considerations`).then((x) => setConsiderations(x.data.considerations)).catch(() => setConsiderations([]));
    } catch (e) {
      setError(e.response?.status === 404 ? "not_found" : "error");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (error === "not_found")
    return <EmptyState icon={ShieldAlert} title="Insight not found"
      description="This Insight doesn't exist or you don't have permission to view it." />;
  if (error) return <EmptyState icon={ShieldAlert} title="Unable to load" description="Please try again shortly." />;
  if (!insight) return <Loading label="Loading Insight…" />;

  const isCreator = insight.created_by === user?.user_id;
  // The Insight's own creator can always rework (edit + resubmit) their draft/returned item.
  const canWrite = isApprover || isCreator || (hasMinRole(user?.role, "analyst") && insight.department_id === user?.department_id);
  const editable = canWrite && (isEditableWorkflow(insight.workflow_status) || isAdmin);
  const transitions = WORKFLOW[insight.workflow_status] ? true : false; // presence check
  const wf = insight.workflow_status;

  const doTransition = async (target, requireNote, override = false) => {
    if (requireNote && !note.trim()) { toast.error("A note is required for this action."); return; }
    setBusy(true);
    try {
      await api.post(`/insights/${id}/transition`, { target_status: target, note: note.trim(), override });
      toast.success("Workflow updated");
      setDialog(null); setNote("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/insights/${id}`, {
        title: edit.title.trim(), summary: edit.summary.trim(), content: edit.content,
        change_summary: "Edited via workspace",
      });
      toast.success("Changes saved");
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name);
    setBusy(true);
    try {
      await api.post(`/insights/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attachment added");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (att) => {
    try {
      const res = await api.get(`/insights/${id}/attachments/${att.attachment_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = att.filename;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed."); }
  };

  return (
    <div data-testid="insight-detail-page">
      <Link to="/insights" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-navy mb-3">
        <ArrowLeft className="h-4 w-4" /> Back to Insights
      </Link>

      {/* HEADER */}
      <Card className="mb-5" data-testid="insight-header">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <WorkflowBadge status={wf} />
              <VisibilityBadge visibility={insight.visibility} />
              {isFinalized(wf) && (
                <span data-testid="finalized-chip" className="inline-flex items-center gap-1 rounded-full border border-brand-teal/40 bg-brand-teal-50 px-2.5 py-0.5 text-xs font-semibold text-brand-teal">
                  <Lock className="h-3 w-3" /> Finalized — workflow complete
                </span>
              )}
            </div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-brand-ink mt-2">{insight.title}</h1>
            <p className="text-sm text-slate-500 mt-1">
              {insight.insight_type_code} · Department {insight.department_id?.replace("dept_", "")} ·
              Created by {insight.created_by_name} · Workflow template v{insight.workflow_template_version}
            </p>
          </div>
          {editable && !editing && (
            <button data-testid="insight-edit-btn" onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-brand-line px-3 py-2 text-sm font-semibold text-brand-navy hover:bg-brand-bg">
              <Pencil className="h-4 w-4" /> Edit
            </button>
          )}
        </div>
        <div className="mt-4 pt-4 border-t border-brand-line">
          <StatusDomainStrip insight={insight} />
        </div>
        {wf === "rejected" && (
          <div data-testid="returned-banner" className="mt-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            <RotateCcw className="h-4 w-4 mt-0.5 shrink-0" />
            <span>This Insight was <strong>returned for revision</strong>. Make the requested changes, then resubmit. Previous history is preserved below.</span>
          </div>
        )}
      </Card>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-5">
          {/* CONTENT */}
          <Card data-testid="insight-content">
            <SectionTitle icon={FileText} title="Content" />
            {editing ? (
              <div className="space-y-3">
                <input data-testid="edit-title" value={edit.title} onChange={(e) => setEdit((s) => ({ ...s, title: e.target.value }))}
                  className="w-full h-10 rounded-lg border border-brand-line px-3 text-sm outline-none focus:border-brand-civic" />
                <textarea data-testid="edit-summary" value={edit.summary} rows={2} onChange={(e) => setEdit((s) => ({ ...s, summary: e.target.value }))}
                  className="w-full rounded-lg border border-brand-line p-3 text-sm outline-none focus:border-brand-civic" />
                {Object.entries(insight.content || {}).map(([k]) => (
                  <div key={k}>
                    <label className="text-xs font-semibold text-slate-500 capitalize">{k.replace(/_/g, " ")}</label>
                    <textarea data-testid={`edit-content-${k}`} rows={3} value={edit.content[k] || ""}
                      onChange={(e) => setEdit((s) => ({ ...s, content: { ...s.content, [k]: e.target.value } }))}
                      className="w-full rounded-lg border border-brand-line p-3 text-sm outline-none focus:border-brand-civic" />
                  </div>
                ))}
                <div className="flex gap-2">
                  <button data-testid="edit-save" disabled={busy} onClick={saveEdit}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900 disabled:opacity-40">
                    <Save className="h-4 w-4" /> Save
                  </button>
                  <button disabled={busy} onClick={() => { setEditing(false); load(); }}
                    className="rounded-lg border border-brand-line px-4 py-2 text-sm font-medium text-slate-600 hover:bg-brand-bg">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {insight.summary && <p className="text-sm text-brand-ink">{insight.summary}</p>}
                {Object.entries(insight.content || {}).length === 0
                  ? <p className="text-sm text-slate-400">No content fields yet.</p>
                  : Object.entries(insight.content || {}).map(([k, v]) => (
                    <div key={k}>
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 capitalize">{k.replace(/_/g, " ")}</div>
                      <p className="text-sm text-brand-ink whitespace-pre-wrap mt-0.5">{v || "—"}</p>
                    </div>
                  ))}
              </div>
            )}
          </Card>

          {/* ATTACHMENTS */}
          <Card data-testid="insight-attachments">
            <div className="flex items-center justify-between mb-3">
              <SectionTitle icon={Paperclip} title="Attachments" noMargin />
              {canWrite && !isTerminal(wf) && (
                <>
                  <input ref={fileRef} type="file" className="hidden" data-testid="attachment-input"
                    onChange={(e) => uploadFile(e.target.files?.[0])} />
                  <button data-testid="attachment-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-brand-line px-3 py-1.5 text-xs font-semibold text-brand-navy hover:bg-brand-bg disabled:opacity-40">
                    <Upload className="h-3.5 w-3.5" /> Upload
                  </button>
                </>
              )}
            </div>
            {attachments.length === 0
              ? <p className="text-sm text-slate-400">No attachments.</p>
              : (
                <div className="space-y-2">
                  {attachments.map((a) => (
                    <div key={a.attachment_id} data-testid="attachment-row"
                      className="flex items-center gap-3 rounded-lg border border-brand-line px-3 py-2">
                      <FileText className="h-4 w-4 text-brand-civic shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-brand-ink truncate">{a.title} <span className="text-xs text-slate-400">v{a.version}</span></div>
                        <div className="text-xs text-slate-400">{a.doc_type} · {(a.size / 1024).toFixed(0)} KB · {a.uploaded_by_name} · {fmtDateTime(a.created_at)}</div>
                      </div>
                      <VisibilityBadge visibility={a.visibility} />
                      <button data-testid="attachment-download" onClick={() => download(a)} className="text-slate-400 hover:text-brand-navy" aria-label="Download">
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
          </Card>

          {/* HISTORY */}
          <Card data-testid="insight-activity">
            <SectionTitle icon={History} title="Activity History" />
            {events.length === 0 ? <p className="text-sm text-slate-400">No activity yet.</p> : (
              <ol className="space-y-3">
                {events.map((ev) => (
                  <li key={ev.event_id} className="flex gap-3" data-testid="activity-item">
                    <span className="mt-1 h-2 w-2 rounded-full bg-brand-civic shrink-0" />
                    <div>
                      <p className="text-sm text-brand-ink">
                        <strong>{ev.actor_name || "Someone"}</strong> {EVENT_LABELS[ev.event_type] || ev.event_type}
                        {ev.detail?.to && <> → <span className="font-medium">{WORKFLOW[ev.detail.to]?.label || ev.detail.to}</span></>}
                        {ev.detail?.override && <span className="ml-1 text-xs font-bold text-red-600">(admin override)</span>}
                      </p>
                      {ev.detail?.note && <p className="text-xs text-slate-500 italic">“{ev.detail.note}”</p>}
                      <p className="text-xs text-slate-400">{fmtDateTime(ev.created_at)}</p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>

        {/* SIDEBAR: WORKFLOW + REVISIONS */}
        <div className="space-y-5">
          {(agendaInfo.length > 0 || considerations.length > 0) && (
            <Card data-testid="insight-meetings">
              <SectionTitle icon={CalendarClock} title="Meetings" />
              {agendaInfo.filter((p) => !p.outcome).map((p) => (
                <div key={p.meeting_id} data-testid="upcoming-meeting" className="rounded-lg border border-brand-civic/30 bg-brand-civic-50 p-3 mb-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-brand-civic">Upcoming Meeting</div>
                  <div className="text-sm font-semibold text-brand-ink mt-0.5">{p.meeting_title}</div>
                  <div className="text-xs text-slate-500">{fmtDateTime(p.date)}</div>
                  <Link to={`/meetings/${p.meeting_id}`} className="text-xs font-semibold text-brand-navy hover:underline mt-1 inline-block">View Meeting →</Link>
                </div>
              ))}
              {considerations.length > 0 && (
                <div className="mt-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Meeting History</div>
                  <ol className="space-y-1.5">
                    {considerations.map((c) => (
                      <li key={c.consideration_id} data-testid="consideration-item" className="text-sm">
                        <span className="font-medium text-brand-ink">{c.governing_body_name}</span>
                        <span className="text-slate-400"> · {fmtDateTime(c.meeting_date)}</span>
                        <span className="ml-1 inline-flex rounded-full border border-brand-teal/30 bg-brand-teal-50 px-2 py-0.5 text-[10px] font-semibold text-brand-teal">{(OUTCOME_LABELS[c.outcome] || c.outcome)}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </Card>
          )}

          {insight.workflow_engine === "advanced" ? (
            <AdvancedWorkflowPanel insightId={id} user={user} insight={insight} reload={load} />
          ) : (
          <Card data-testid="workflow-panel">
            <SectionTitle icon={GitBranch} title="Workflow" />
            <ol className="space-y-2 mb-4">
              {STAGE_ORDER.map((st) => {
                const idx = STAGE_ORDER.indexOf(wf);
                const here = st === wf;
                const done = STAGE_ORDER.indexOf(st) < idx && !["rejected", "denied"].includes(wf);
                return (
                  <li key={st} className="flex items-center gap-2 text-sm">
                    <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold
                      ${here ? "bg-brand-navy text-white" : done ? "bg-brand-teal text-white" : "bg-slate-100 text-slate-400"}`}>
                      {done ? <CheckCircle2 className="h-3 w-3" /> : STAGE_ORDER.indexOf(st) + 1}
                    </span>
                    <span className={here ? "font-semibold text-brand-ink" : "text-slate-500"}>{WORKFLOW[st].label}</span>
                    {here && <span className="ml-auto text-[10px] uppercase tracking-wider text-brand-civic font-bold">current</span>}
                  </li>
                );
              })}
              {(wf === "rejected" || wf === "denied") && (
                <li className="flex items-center gap-2 text-sm">
                  <span className="h-5 w-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-[10px] font-bold">!</span>
                  <span className="font-semibold text-red-600">{WORKFLOW[wf].label}</span>
                </li>
              )}
            </ol>

            {/* ACTIONS */}
            <div className="space-y-2">
              {/* Creator submit / resubmit */}
              {canWrite && (wf === "draft" || wf === "rejected") && (
                <ActionBtn testid="wf-submit" icon={Send} label={wf === "rejected" ? "Resubmit" : "Submit for Review"}
                  onClick={() => doTransition("submitted", false)} disabled={busy} />
              )}
              {/* Approver moves submitted -> in_review */}
              {isApprover && wf === "submitted" && (
                <ActionBtn testid="wf-start-review" icon={GitBranch} label="Begin Review"
                  onClick={() => doTransition("in_review", false)} disabled={busy} />
              )}
              {/* Approver decisions during in_review */}
              {isApprover && wf === "in_review" && (
                <>
                  <ActionBtn testid="wf-approve" icon={CheckCircle2} label="Approve" primary
                    onClick={() => setDialog({ action: "approve" })} disabled={busy} />
                  <ActionBtn testid="wf-return" icon={RotateCcw} label="Return for Revision"
                    onClick={() => setDialog({ action: "return" })} disabled={busy} />
                  <ActionBtn testid="wf-deny" icon={XCircle} label="Deny" danger
                    onClick={() => setDialog({ action: "deny" })} disabled={busy} />
                </>
              )}
              {/* Administrative override — visually separated */}
              {isAdmin && isTerminal(wf) && (
                <div className="pt-2 mt-2 border-t border-dashed border-red-200">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-red-500 mb-1.5 flex items-center gap-1">
                    <ShieldAlert className="h-3 w-3" /> Administrative Action
                  </p>
                  <ActionBtn testid="wf-override" icon={ShieldAlert} label="Override → Reopen to Draft" danger
                    onClick={() => setDialog({ action: "override" })} disabled={busy} />
                </div>
              )}
              {!canWrite && !isApprover && (
                <p className="text-xs text-slate-400">You have read-only access to this Insight.</p>
              )}
            </div>
          </Card>
          )}

          {/* REVISIONS */}
          <Card data-testid="insight-revisions">
            <SectionTitle icon={Clock} title="Revision History" />
            {revisions.length === 0 ? <p className="text-sm text-slate-400">No revisions.</p> : (
              <ol className="space-y-2">
                {revisions.map((r, i) => (
                  <li key={r.revision_id} data-testid="revision-item" className="flex items-center gap-2 text-sm">
                    <span className="text-xs font-bold text-brand-navy w-8">v{r.revision_number}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-brand-ink truncate">{r.change_summary || "—"}</div>
                      <div className="text-xs text-slate-400">{r.changed_by_name} · {fmtDateTime(r.created_at)}</div>
                    </div>
                    {i === 0 && <span className="text-[10px] font-bold uppercase text-brand-teal">current</span>}
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>
      </div>

      {/* Confirmation dialogs (never optimistic) */}
      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) { setDialog(null); setNote(""); } }}>
        <DialogContent data-testid="workflow-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialog?.action === "approve" && "Approve Insight"}
              {dialog?.action === "return" && "Return for Revision"}
              {dialog?.action === "deny" && "Deny Insight"}
              {dialog?.action === "override" && "Administrative Override"}
            </DialogTitle>
            <DialogDescription>
              {dialog?.action === "approve" && "Approving finalizes the staff workflow. The Insight becomes read-only. This does not publish it or imply a governing-body decision."}
              {dialog?.action === "return" && `This returns the Insight to ${insight.created_by_name} for revision. A note is required.`}
              {dialog?.action === "deny" && "Denial is terminal. A note is required. This does not imply a governing-body decision."}
              {dialog?.action === "override" && "This administrative action reopens a finalized Insight to Draft. Prior approvals may need to be repeated. A note is required."}
            </DialogDescription>
          </DialogHeader>
          {dialog?.action !== "approve" && (
            <textarea data-testid="dialog-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              placeholder="Reason / note…" className="w-full rounded-lg border border-brand-line p-3 text-sm outline-none focus:border-brand-civic" />
          )}
          <DialogFooter>
            <button onClick={() => { setDialog(null); setNote(""); }} className="rounded-lg border border-brand-line px-4 py-2 text-sm font-medium text-slate-600 hover:bg-brand-bg">Cancel</button>
            <button data-testid="dialog-confirm" disabled={busy}
              onClick={() => {
                if (dialog.action === "approve") doTransition("approved", false);
                if (dialog.action === "return") doTransition("rejected", true);
                if (dialog.action === "deny") doTransition("denied", true);
                if (dialog.action === "override") doTransition("draft", true, true);
              }}
              className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900 disabled:opacity-40">
              Confirm
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SectionTitle({ icon: Icon, title, noMargin }) {
  return (
    <div className={`flex items-center gap-2 ${noMargin ? "" : "mb-3"}`}>
      <Icon className="h-4 w-4 text-brand-civic" />
      <h2 className="font-display font-bold text-brand-ink text-base">{title}</h2>
    </div>
  );
}

function ActionBtn({ testid, icon: Icon, label, onClick, disabled, primary, danger }) {
  const cls = primary
    ? "bg-brand-teal text-white hover:bg-brand-teal/90 border-brand-teal"
    : danger
      ? "bg-white text-red-600 border-red-200 hover:bg-red-50"
      : "bg-brand-navy text-white hover:bg-brand-navy-900 border-brand-navy";
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-40 ${cls}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}
